import Image from 'next/image';
import { FunctionComponent, useState, useCallback } from 'react';
import { isMobile } from 'react-device-detect';

import { addFavourite } from '@/services/FavouritesApi';
import DataLayer from '@/utils/DataLayer';

export type CardColor = 'orange' | 'cream' | 'white';

interface RecommendationCardProps {
  cardColor: CardColor;
  image: string;
  title: string;
  description: string;
  url: string;
  entityType: 'program' | 'movie';
  id: string;
  cardIndex: number;
  enableFlipAnimation?: boolean;
  isFavourite: boolean;
}

const RecommendationCard: FunctionComponent<RecommendationCardProps> = ({
  cardColor, image, title, description,
  url, entityType, id, cardIndex,
  enableFlipAnimation = false,
  isFavourite,
}) => {
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);

  const handleAddFavourite = async (type: 'program' | 'movie', itemId: string) => {
    try {
      setLoading(true);
      const result = await addFavourite(type, itemId);
      setDisabled(result);
    } finally {
      setLoading(false);
    }
  };

  const flipCard = useCallback(() => {
    const cardInner = document.getElementById(`flip-card-inner-${cardIndex}`);

    if (cardInner) {
      if (cardInner.style.transform === 'rotateY(180deg)') {
        cardInner.style.transform = '';
        setIsFlipped(false);
      } else if (cardInner) {
        cardInner.style.transform = 'rotateY(180deg)';
        setIsFlipped(true);
      }
    }
  }, [cardIndex]);

  const handleCardClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!enableFlipAnimation || (event.target as HTMLElement).classList.contains('button')) {
      return;
    }

    if (isMobile) {
      flipCard();
    }
  }, [enableFlipAnimation, flipCard]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      // Cancel the default action, if needed
      event.preventDefault();
      flipCard();
    }
  }, [flipCard]);

  const handleBlur = useCallback(() => {
  }, []);

  const handleMouseOver = useCallback(() => {
    if (!isMobile) {
      setIsFlipped(true);
      const cardInner = document.getElementById(`flip-card-inner-${cardIndex}`);

      if (cardInner) {
        cardInner.style.transform = 'rotateY(180deg)';
      }
    }
  }, [cardIndex]);

  const handleMouseOut = useCallback(() => {
    if (!isMobile) {
      setIsFlipped(false);

      const cardInner = document.getElementById(`flip-card-inner-${cardIndex}`);

      if (cardInner) {
        cardInner.style.transform = '';
      }
    }
  }, [cardIndex]);

  const handleFavouriteClick = useCallback(() => {
    DataLayer.events.addToFavourites(cardIndex + 1);
    return handleAddFavourite(entityType, id);
  }, [cardIndex, entityType, id]);

  const handleMoreInfoClick = useCallback(() => {
    DataLayer.events.recommendationMoreInfo();
  }, []);

  let descriptionBgColor;
  switch (cardColor) {
    case 'white':
      descriptionBgColor = 'bg-white';
      break;

    case 'cream':
      descriptionBgColor = 'bg-blanched-almond';
      break;

    default:
      descriptionBgColor = 'bg-dark-tangerine';
      break;
  }

  const backgroundCard = (
    <Image
      className="absolute left-0 !w-[292px] !h-[400px] lg:!w-[355px] lg:!h-[485px]"
      src={`/images/tarot-card-${cardColor}.png`}
      alt="Your recommendation"
      width={712}
      height={970}
    />
  );

  return (
    <div
      role="button"
      className="flip-card perspective-[1000px] w-[292px] h-[400px] lg:w-[355px] lg:h-[485px] xl-short:scale-[80%]"
      onMouseUp={handleCardClick}
      onKeyDown={handleKeyDown}
      onMouseOver={handleMouseOver}
      onFocus={() => {}}
      onMouseOut={handleMouseOut}
      onBlur={handleBlur}
      tabIndex={0}
    >
      <div
        id={`flip-card-inner-${cardIndex}`}
        className={`flip-card-inner relative w-full h-full text-center
          transition-transform duration-700 transform-style-3d`}
      >
        {enableFlipAnimation && (
          <>
            <div className="flip-card-front absolute w-full h-full backface-hidden">
              {backgroundCard}
              <Image
                className="absolute top-[39px] left-[39px] lg:top-[45px] lg:left-[47px] !w-[213px] !h-[320px] lg:!w-[260px] lg:!h-[390px] rounded-md"
                src={image}
                alt={title}
                width={600}
                height={900}
              />
            </div>
            <div
              className="flip-card-back text-black absolute w-full h-full backface-hidden rotate-y-180"
              aria-hidden={!isFlipped}
            >
              {backgroundCard}
              <div
                className={`text-sm lg:text-base absolute left-[50%] !w-[227px] !h-[333px] lg:!w-[270px] lg:!h-[400px] ${descriptionBgColor} p-4
              translate-x-[-50%] top-[50%] translate-y-[-50%]`}
              >
                <p
                  className="text-sm lg:text-base max-h-[230px] overflow-y-auto"
                  onWheelCapture={(event) => { event.stopPropagation(); }}
                  onTouchMoveCapture={(event) => { event.stopPropagation(); }}
                >
                  {description}
                </p>
                <button
                  type="button"
                  className="button bg-black text-white border-black border-2 mt-8 h-[48px] px-4 rounded-3xl hover:bg-grey disabled:pointer-events-none w-full"
                  onClick={handleFavouriteClick}
                  onKeyDown={(e) => { e.stopPropagation(); }}
                  tabIndex={isFlipped ? 0 : -1}
                  disabled={loading || disabled || isFavourite}
                  aria-disabled={disabled || isFavourite}
                >
                  {!isFavourite && !disabled && !loading && 'Add to Favourites'}
                  {(isFavourite || disabled) && !loading && 'Added to Favourites!'}
                  {loading && 'Loading...'}
                </button>
                <a
                  className="button bg-dark-tangerine hover:bg-golden-tainoi text-black border-black border-2 mt-4 h-[48px] px-4 lg:px-8 rounded-3xl w-full"
                  href={`${url}?cid=rewind-recommendations`}
                  target="_blank"
                  onClick={handleMoreInfoClick}
                  tabIndex={isFlipped ? 0 : -1}
                  onKeyDown={(e) => { e.stopPropagation(); }}
                >
                  More info
                </a>
              </div>
            </div>
          </>
        )}
        {!enableFlipAnimation && (
          <>
            <div className="flip-card-background absolute w-full h-full">
              <Image
                className="absolute left-0 !w-[292px] !h-[400px] md:!w-[355px] md:!h-[485px]"
                src={`/images/tarot-card-${cardColor}.png`}
                alt="Your recommendation"
                width={712}
                height={970}
              />
            </div>
            <a
              className="block absolute"
              target="_blank"
              href={`${url}?cid=rewind-recommendations`}
            >
              <Image
                className="absolute top-[39px] left-[39px] lg:top-[45px] lg:left-[47px] !w-[213px] !h-[320px] lg:!w-[260px] lg:!h-[390px] rounded-md"
                src={image}
                alt={title}
                width={600}
                height={900}
              />
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default RecommendationCard;
